import styled from "@emotion/styled";
import HeaderLayout from "components/header-layout";
import { useRefreshUser, useSafeCurrentUser } from "redux/user";
import { getImageUrl } from "utils";
import debounce from "lodash/debounce";
import EditUserAvatarButton from "components/profile/edit-user-avatar-button";
import UserInfoSection from "components/profile/user-info-section";
import UserNotificationsSection from "components/profile/user-notifications-section";
import { useCallback, useRef, useState } from "react";
import { updateUser } from "services/user";
import { pushProfileImage } from "services/storage";
import TrialBanner from "components/trial-banner";

const Container = styled.div`
    width: clamp(400px, 50%, 500px);
    min-height: 100%;
    display: flex;
    flex-direction: column;
    padding: 2rem 1rem 0rem 1rem;
    margin: 0 auto 16px;
`;

const Profile = () => {
    const user = useSafeCurrentUser();
    const initialUser = useRef({
        name: user.name,
        is_keep_in_touch_email: user.is_keep_in_touch_email,
        is_unsubscribe: user.is_unsubscribe,
        send_emails: user.send_emails,
        send_notifications: user.send_notifications,
        numberOfRandomFacts: user.numberOfRandomFacts,
        numberOfLocationFacts: user.numberOfLocationFacts,
        notifTimePreferred: user.notifTimePreferred,
        profile: user.profile,
    });

    const refreshUser = useRefreshUser(user.google_id);

    const [draftUser, setDraftUser] = useState({
        ...initialUser.current,
    });

    const [image, setImage] = useState<string | null>(null);

    const debounceFn = useRef(
        debounce(async (updatedUser) => {
            await updateUser(user.user_id, updatedUser);
            refreshUser();
        }, 1000)
    ).current;

    const handleChange = useCallback(
        (field: keyof typeof draftUser) => (value: string) => {
            setDraftUser({ ...draftUser, [field]: value });
            debounceFn({ ...draftUser, [field]: value });
        },
        [debounceFn, draftUser]
    );

    const handleImageUpload = useCallback(
        async (file: File) => {
            setImage(URL.createObjectURL(file));

            const newProfile = await pushProfileImage(file);

            await updateUser(user.user_id, { profile: newProfile });
            refreshUser();
        },
        [refreshUser, user.user_id]
    );

    return (
        <HeaderLayout>
            <Container>
                <div className="self-center mb-8 mx-auto">
                    <EditUserAvatarButton
                        src={image || getImageUrl(draftUser?.profile || "")}
                        alt={user?.name}
                        onUpload={handleImageUpload}
                    />
                </div>
                <UserInfoSection
                    name={draftUser?.name}
                    email={user?.email}
                    onNameChange={handleChange("name")}
                />
                <UserNotificationsSection
                    is_keep_in_touch_email={draftUser.is_keep_in_touch_email}
                    is_unsubscribe={draftUser.is_unsubscribe}
                    send_emails={draftUser.send_emails}
                    send_notifications={draftUser.send_notifications}
                    numberOfRandomFacts={draftUser.numberOfRandomFacts}
                    numberOfLocationFacts={draftUser.numberOfLocationFacts}
                    notifTimePreferred={draftUser.notifTimePreferred}
                    handleChange={handleChange}
                />
            </Container>
            <TrialBanner />
        </HeaderLayout>
    );
};

export default Profile;
